const RELOAD_TRIGGER_ATTRIBUTES = new Set([
  'data-product-id',
  'data-section-id',
  'data-intent',
  'data-url',
]);

class ProductRecommendations extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      const { attributeName } = mutation;
      if (!attributeName || !RELOAD_TRIGGER_ATTRIBUTES.has(attributeName)) continue;

      if (
        attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue;
      }

      this.#loadRecommendations();
      break;
    }
  });

  /** @type {Record<string, string>} */
  #cachedRecommendations = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    this.#ensureVisibleIfContent();

    this.#mutationObserver.observe(this, { attributes: true });

    if (this.dataset.recommendationsPerformed === 'true' && this.#hasRecommendations()) {
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  #hasRecommendations() {
    return this.querySelector('[data-has-recommendations="true"]') !== null;
  }

  #ensureVisibleIfContent() {
    if (!this.#hasRecommendations()) return false;

    const hadStaleState = this.classList.contains('hidden') || this.dataset.error !== undefined;

    if (hadStaleState) {
      this.classList.remove('hidden');
      delete this.dataset.error;
    }

    return hadStaleState;
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (recommendationsPerformed === 'true') {
      this.#ensureVisibleIfContent();
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#ensureVisibleIfContent();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {Error} error
   */
  #handleError(error) {
    if (this.#hasRecommendations()) {
      this.#ensureVisibleIfContent();
      return;
    }

    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  for (const element of document.querySelectorAll('product-recommendations')) {
    if (!(element instanceof HTMLElement)) continue;
    if (!element.querySelector('[data-has-recommendations="true"]')) continue;

    element.classList.remove('hidden');
    delete element.dataset.error;
  }
});
